import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const Churdhar = () => {
  const tourData = {
    name: "Churdhar",
    duration: "03 Nights 04 Days",
    price: "₹ 5,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1755111158/xkx4mvpknsx4fip3pzdp.webp",
    altitude: "7,000 ft",
    difficulty: "Moderate",
    region: "Himachal Pradesh",
    highlights: ["Churdhar trek", "Chopal village", "Mountain views"],
    bestTime: "March to November",
    description: "The Churdhar tour is a perfect weekend getaway in Himachal Pradesh that combines trekking with spiritual experiences. This moderate tour offers Churdhar Mahadev Temple trek, Chopal village exploration, and scenic mountain views, making it ideal for adventure enthusiasts."
  };

  return (
    <>
      <Helmet>
        <title>Churdhar Tour - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Churdhar tour in Himachal Pradesh. Weekend getaway with Triund trek and paragliding experiences." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Weekend getaway with Churdhar trek and spiritual experiences
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This tour takes you to the beautiful Churdhar Mahadev Temple in Himachal Pradesh, 
                    offering a perfect blend of trekking and spiritual experiences. The journey includes 
                    Churdhar trek, Chopal village exploration, and Shimla visit, making it an ideal 
                    weekend escape.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Churdhar Mahadev Temple trek</li>
                    <li>Chopal village exploration</li>
                    <li>Shimla Mall Road visit</li>
                    <li>Mountain views and meadows</li>
                    <li>Evening aarti experience</li>
                    <li>Moderate difficulty level</li>
                    <li>Perfect for adventure enthusiasts</li>
                    <li>Spiritual experiences</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 0: Depart from Delhi</h4>
                    <ul>
                      <li>Depart from Delhi around 10 PM in a comfy luxury tempo traveler, Volvo, or car</li>
                      <li>Our first stop? Dinner break to fuel up for the road trip</li>
                      <li>Get ready for a night full of fun—Antakshari, dumb charades, dance-offs, and loads of interactive games to kickstart the adventure!</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 1: Explore Chopal</h4>
                    <ul>
                      <li>Arrive at Chopal village and check into the hotel</li>
                      <li>Freshen up and enjoy a hearty breakfast</li>
                      <li>Time to unwind—take a short stroll around the village, soaking in the scenic vibes</li>
                      <li>Lunch at the hotel to recharge</li>
                      <li>The afternoon is yours—explore local sights, chill out, or simply relax</li>
                      <li>Evening tea & snacks, followed by some free time to prep for the trek ahead</li>
                      <li>Dinner and overnight stay at the hotel in Chopal</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Trek to Churdhar</h4>
                    <ul>
                      <li>Fuel up with breakfast, then hit the trail to Churdhar Mahadev Temple (9 km / 4-5 hrs)</li>
                      <li>Enjoy scenic views of meadows, forests, and mountains on a moderate trek</li>
                      <li>Reach the temple at 3,647 meters, explore, meditate, and soak in the peace</li>
                      <li>Check-in to a guesthouse, attend the evening aarti, and end the day with dinner and rest</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Explore & Head Back</h4>
                    <ul>
                      <li>Morning aarti with a Himalayan sunrise</li>
                      <li>Breakfast, then explore the temple area</li>
                      <li>Begin your descent to Shimla, stroll around Mall Road, and enjoy the vibe</li>
                      <li>By evening, start your return journey to Delhi, reaching the next morning</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Destinations: Churdhar and Shimla</li>
                    <li>Stay at a hotel in Chopal</li>
                    <li>4 meals: 2 breakfasts + 2 dinners</li>
                    <li>Round-trip travel from Delhi</li>
                    <li>Bonfire and live guitar session</li>
                    <li>Mineral water during travel</li>
                    <li>Professional trekking guide</li>
                    <li>24/7 customer support</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Costs due to natural calamities or any unforeseen situations beyond our control</li>
                    <li>Any other expenses not mentioned under the Inclusions section</li>
                    <li>Personal expenses and tips</li>
                    <li>Travel insurance</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>7,000 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Tour Distance:</strong>
                      <span>600 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>March to November</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person</h4>
                  <ul>
                    <li><strong>QUAD SHARING:</strong> ₹ 5,999 + 5% GST</li>
                    <li><strong>TRIPLE SHARING:</strong> ₹ 6,499 + 5% GST</li>
                    <li><strong>DOUBLE SHARING:</strong> ₹ 6,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Delhi:</strong> ISBT Kashmere Gate b/w 09:00 PM to 10:00 PM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Churdhar Mahadev Temple trek</li>
                    <li>Chopal village exploration</li>
                    <li>Shimla Mall Road visit</li>
                    <li>Mountain views and meadows</li>
                    <li>Evening aarti experience</li>
                    <li>Moderate difficulty level</li>
                    <li>Perfect for adventure enthusiasts</li>
                    <li>Spiritual experiences</li>
                    <li>Professional guide support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Churdhar;
